/**
 * Клиент кассового агента на этом же компьютере (репозиторий Kalmykov-Cash-Agent, docs/PROTOCOL.md).
 *
 * Это ВТОРОЕ место клиента, где вообще вызывается `fetch` (первое — transport к шлюзу): агент слушает только
 * `127.0.0.1` в диапазоне портов, поэтому адрес не «тот же origin», и общий transport (cookie, CSRF, шлюз) для него
 * не подходит. Правило «HTTP только через transport» здесь сознательно снято для одного файла; всё остальное
 * (экраны, features) по-прежнему ходит к агенту ТОЛЬКО через эти функции, а не через свой `fetch`.
 *
 * Поиск: окно спрашивает `GET /v1/hello` сначала на последнем рабочем порту, потом на всём диапазоне параллельно
 * (таймаут 300 мс) — агент занимает первый свободный порт и мог уйти на следующий, если первый был занят.
 */

/** Диапазон портов агента (docs/PROTOCOL.md): 47770–47779. */
export const LOCAL_AGENT_FIRST_PORT = 47770
export const LOCAL_AGENT_PORT_COUNT = 10
export const LOCAL_AGENT_PORTS: readonly number[] = Array.from(
  { length: LOCAL_AGENT_PORT_COUNT },
  (_, index) => LOCAL_AGENT_FIRST_PORT + index,
)

/** Признак нашего агента в ответе `/v1/hello`: чужая программа на том же порту агентом не считается. */
export const LOCAL_AGENT_PRODUCT = 'kalmykov-cash-agent'

const PROBE_TIMEOUT_MS = 300
const REQUEST_TIMEOUT_MS = 8_000

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

export interface LocalAgentClientOptions {
  /** Подставной fetch (тесты). По умолчанию — глобальный. */
  fetch?: FetchLike
  probeTimeoutMs?: number
  requestTimeoutMs?: number
}

export type LocalAgentErrorKind =
  /** Агент не ответил: не запущен, порт другой, связь оборвалась. */
  | 'unreachable'
  /** Нужен токен или он не подошёл (401). */
  | 'unauthorized'
  /** Агент понял запрос и отказал: русский текст отказа — в `message`. */
  | 'refused'
  /** Ответ не того вида, что ждёт клиент. */
  | 'invalid-response'

export interface LocalAgentError {
  kind: LocalAgentErrorKind
  message: string
  status?: number
  /** Машинный код отказа из `error.code` тела ответа агента (например `equipment-refused`); нет, если агент его не дал. */
  code?: string
  /** Через сколько секунд повторить (`error.retryAfter` или заголовок `Retry-After`) — у технической занятости `device-busy`. */
  retryAfterSeconds?: number
  /** Что занято (`error.device`): `terminal` или `fiscal`. */
  busyDevice?: string
}

/** Ошибка запроса к агенту: настоящий Error (с трассой), а не россыпь полей; `kind`, `status` и `code` — для разбора на экране. */
export class LocalAgentRequestError extends Error implements LocalAgentError {
  readonly kind: LocalAgentErrorKind
  readonly status?: number
  readonly code?: string
  readonly retryAfterSeconds?: number
  readonly busyDevice?: string

  constructor(kind: LocalAgentErrorKind, message: string, status?: number, code?: string, busy?: { retryAfterSeconds?: number; device?: string }) {
    super(message)
    this.name = 'LocalAgentRequestError'
    this.kind = kind
    this.status = status
    this.code = code
    this.retryAfterSeconds = busy?.retryAfterSeconds
    this.busyDevice = busy?.device
  }
}

export function isLocalAgentError(value: unknown): value is LocalAgentError {
  return value instanceof LocalAgentRequestError
}

export interface LocalAgentRequest<T> {
  port: number
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  path: string
  body?: unknown
  /** Токен рабочего места (команды кассе). Для чтения/настройки оборудования агент пускает и по адресу сайта. */
  token?: string
  decode: (raw: unknown) => T
  /** Предел ожидания ответа, мс. Не задан — как в настройках клиента (8 с); долгие запросы (скан кассы) задаёт вызывающий. */
  timeoutMs?: number
}

export interface DiscoveredAgent<THello> {
  port: number
  hello: THello
}

/**
 * Порт ответил, но тело урезано: агент не пустил этот сайт (`originAllowed:false`) или отказал статусом 403.
 * Клиент не знает форму урезанного hello (она в контракте агента), поэтому разбирает его колбэк вызывающего.
 */
export interface RestrictedAgent<TRestricted> {
  port: number
  restricted: TRestricted
}

/** Исход опроса одного порта. */
export type ProbeOutcome<THello, TRestricted = never> =
  | { kind: 'agent'; agent: DiscoveredAgent<THello> }
  | { kind: 'restricted'; restricted: RestrictedAgent<TRestricted> }
  /** Порт молчит или там не наш агент. */
  | { kind: 'silent' }

/**
 * Итог полного просмотра диапазона. Пустые `agents` и `restricted` — «агента нет»; пустые `agents` при непустых
 * `restricted` — «агент есть, но сайт не пущен».
 */
export interface PortScan<THello, TRestricted = never> {
  agents: readonly DiscoveredAgent<THello>[]
  restricted: readonly RestrictedAgent<TRestricted>[]
}

/**
 * Разбор урезанного тела: вернуть значение — это наш агент, но сайт не пущен; `null` или исключение — не наш ответ.
 * Вызывается, когда полный разбор не удался или статус не 2xx. Ограничение: если браузер блокирует ответ по CORS,
 * тела нет вовсе и порт неотличим от молчащего — поэтому агент обязан отдавать CORS на hello всегда (проект, п. 7.1).
 */
export type DecodeRestricted<TRestricted> = (raw: unknown, status: number) => TRestricted | null

export interface AgentPreference {
  /** Идентификатор установки агента, выбранного ранее (память страницы). */
  instanceId?: string
  /** Порт, на котором агент отвечал в прошлый раз. */
  port?: number
}

export interface AgentSelection<THello> {
  agent: DiscoveredAgent<THello> | null
  /** Агентов несколько и ни один не подошёл под предпочтение: экран должен спросить, какой взять. */
  ambiguous: boolean
}

/**
 * Выбор агента из найденных. Чистая функция: сперва по `instanceId`, затем по запомненному порту; единственный агент
 * берётся сам; иначе берётся ближайший по номеру порта, но `ambiguous: true` (выбор за человеком).
 */
export function selectAgent<THello>(
  agents: readonly DiscoveredAgent<THello>[],
  preference: AgentPreference = {},
  instanceIdOf?: (hello: THello) => string | undefined,
): AgentSelection<THello> {
  if (agents.length === 0) return { agent: null, ambiguous: false }
  if (preference.instanceId !== undefined && instanceIdOf !== undefined) {
    const byInstance = agents.find((item) => instanceIdOf(item.hello) === preference.instanceId)
    if (byInstance !== undefined) return { agent: byInstance, ambiguous: false }
  }
  if (preference.port !== undefined) {
    const byPort = agents.find((item) => item.port === preference.port)
    if (byPort !== undefined) return { agent: byPort, ambiguous: false }
  }
  const first = [...agents].sort((a, b) => a.port - b.port)[0] ?? null
  return { agent: first, ambiguous: agents.length > 1 }
}

export interface LocalAgentClient {
  /** Один порт: ответил ли там наш агент. */
  probe<THello>(port: number, decode: (raw: unknown) => THello): Promise<DiscoveredAgent<THello> | null>
  /** Один порт с различением «ответил наш агент / ответил урезанно (сайт не пущен) / молчит». */
  probeOutcome<THello, TRestricted = never>(
    port: number,
    decode: (raw: unknown) => THello,
    decodeRestricted?: DecodeRestricted<TRestricted>,
  ): Promise<ProbeOutcome<THello, TRestricted>>
  /** Полный просмотр всего диапазона параллельно, без короткого пути по запомненному порту: видны все агенты. */
  discoverAll<THello>(decode: (raw: unknown) => THello): Promise<readonly DiscoveredAgent<THello>[]>
  /** То же, что `discoverAll`, но ещё и порты, ответившие урезанным телом (сайт не пущен). */
  scanAll<THello, TRestricted>(
    decode: (raw: unknown) => THello,
    decodeRestricted: DecodeRestricted<TRestricted>,
  ): Promise<PortScan<THello, TRestricted>>
  /** Ищет агента: сначала `preferredPort`, потом весь диапазон параллельно. Нашедшихся может быть несколько. */
  discover<THello>(decode: (raw: unknown) => THello, preferredPort?: number): Promise<readonly DiscoveredAgent<THello>[]>
  request<T>(request: LocalAgentRequest<T>): Promise<T>
}

export function createLocalAgentClient(options: LocalAgentClientOptions = {}): LocalAgentClient {
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init))
  const probeTimeout = options.probeTimeoutMs ?? PROBE_TIMEOUT_MS
  const requestTimeout = options.requestTimeoutMs ?? REQUEST_TIMEOUT_MS

  async function withTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await doFetch(url, { ...init, signal: controller.signal })
    } finally {
      clearTimeout(timer)
    }
  }

  const base = (port: number) => `http://127.0.0.1:${port}`

  async function probeOutcome<THello, TRestricted = never>(
    port: number,
    decode: (raw: unknown) => THello,
    decodeRestricted?: DecodeRestricted<TRestricted>,
  ): Promise<ProbeOutcome<THello, TRestricted>> {
    let response: Response
    try {
      response = await withTimeout(`${base(port)}/v1/hello`, { method: 'GET' }, probeTimeout)
    } catch {
      // Порт молчит (или CORS-блок без тела) — для поиска это «здесь агента нет».
      return { kind: 'silent' }
    }
    let raw: unknown
    try {
      raw = await response.json()
    } catch {
      raw = undefined
    }
    if (response.ok) {
      try {
        return { kind: 'agent', agent: { port, hello: decode(raw) } }
      } catch {
        // Полный разбор не удался: возможно, тело урезано — пробуем разобрать как урезанное.
      }
    }
    if (decodeRestricted !== undefined) {
      try {
        const restricted = decodeRestricted(raw, response.status)
        if (restricted !== null) return { kind: 'restricted', restricted: { port, restricted } }
      } catch {
        // Не наш ответ.
      }
    }
    return { kind: 'silent' }
  }

  async function probe<THello>(port: number, decode: (raw: unknown) => THello): Promise<DiscoveredAgent<THello> | null> {
    const outcome = await probeOutcome(port, decode)
    return outcome.kind === 'agent' ? outcome.agent : null
  }

  async function scanAll<THello, TRestricted>(
    decode: (raw: unknown) => THello,
    decodeRestricted: DecodeRestricted<TRestricted>,
  ): Promise<PortScan<THello, TRestricted>> {
    const outcomes = await Promise.all(LOCAL_AGENT_PORTS.map((port) => probeOutcome(port, decode, decodeRestricted)))
    const agents: DiscoveredAgent<THello>[] = []
    const restricted: RestrictedAgent<TRestricted>[] = []
    for (const outcome of outcomes) {
      if (outcome.kind === 'agent') agents.push(outcome.agent)
      else if (outcome.kind === 'restricted') restricted.push(outcome.restricted)
    }
    return { agents, restricted }
  }

  async function discoverAll<THello>(decode: (raw: unknown) => THello): Promise<readonly DiscoveredAgent<THello>[]> {
    const results = await Promise.all(LOCAL_AGENT_PORTS.map((port) => probe(port, decode)))
    return results.filter((item): item is DiscoveredAgent<THello> => item !== null)
  }

  async function discover<THello>(
    decode: (raw: unknown) => THello,
    preferredPort?: number,
  ): Promise<readonly DiscoveredAgent<THello>[]> {
    const remembered = preferredPort !== undefined && LOCAL_AGENT_PORTS.includes(preferredPort) ? preferredPort : undefined
    if (remembered !== undefined) {
      const first = await probe(remembered, decode)
      if (first !== null) return [first]
    }
    const rest = LOCAL_AGENT_PORTS.filter((port) => port !== remembered)
    const results = await Promise.all(rest.map((port) => probe(port, decode)))
    return results.filter((item): item is DiscoveredAgent<THello> => item !== null)
  }

  async function request<T>(spec: LocalAgentRequest<T>): Promise<T> {
    const headers: Record<string, string> = {}
    if (spec.token !== undefined) headers.Authorization = `Bearer ${spec.token}`
    let init: RequestInit = { method: spec.method ?? 'GET', headers }
    if (spec.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      init = { ...init, body: JSON.stringify(spec.body) }
    }

    let response: Response
    try {
      response = await withTimeout(`${base(spec.port)}${spec.path}`, init, spec.timeoutMs ?? requestTimeout)
    } catch {
      throw new LocalAgentRequestError('unreachable', 'Агент на этом компьютере не отвечает.')
    }

    let raw: unknown
    try {
      raw = await response.json()
    } catch {
      raw = undefined
    }
    if (response.status === 401) {
      throw new LocalAgentRequestError('unauthorized', 'Агент не принял токен.', 401)
    }
    if (!response.ok) {
      throw new LocalAgentRequestError('refused', refusalText(raw, response.status), response.status, refusalCode(raw), busyOf(raw, response))
    }
    try {
      return spec.decode(raw)
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Ответ агента не разобран.'
      throw new LocalAgentRequestError('invalid-response', message)
    }
  }

  return { probe, probeOutcome, discover, discoverAll, scanAll, request }
}

/** Занятость аппаратуры из тела `{ error: { retryAfter, device } }` или заголовка `Retry-After` (секунды). */
function busyOf(raw: unknown, response: Response): { retryAfterSeconds?: number; device?: string } | undefined {
  let retryAfter: number | undefined
  let device: string | undefined
  if (typeof raw === 'object' && raw !== null) {
    const error = (raw as { error?: unknown }).error
    if (typeof error === 'object' && error !== null) {
      const seconds = (error as { retryAfter?: unknown }).retryAfter
      if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0) retryAfter = seconds
      const name = (error as { device?: unknown }).device
      if (typeof name === 'string' && name.length > 0) device = name
    }
  }
  const header = response.headers.get('Retry-After')
  if (retryAfter === undefined && header !== null && Number.isFinite(Number(header)) && Number(header) >= 0) retryAfter = Number(header)
  return retryAfter === undefined && device === undefined ? undefined : { retryAfterSeconds: retryAfter, device }
}

/** Машинный код отказа из тела `{ error: { code } }`; нет — `undefined`. */
function refusalCode(raw: unknown): string | undefined {
  if (typeof raw === 'object' && raw !== null) {
    const error = (raw as { error?: unknown }).error
    if (typeof error === 'object' && error !== null) {
      const code = (error as { code?: unknown }).code
      if (typeof code === 'string' && code.length > 0) return code
    }
  }
  return undefined
}

/** Русский текст отказа из тела `{ error: { text } }`; иначе — общий текст со статусом. */
function refusalText(raw: unknown, status: number): string {
  if (typeof raw === 'object' && raw !== null) {
    const error = (raw as { error?: unknown }).error
    if (typeof error === 'object' && error !== null) {
      const text = (error as { text?: unknown }).text
      if (typeof text === 'string' && text.length > 0) return text
    }
  }
  return `Агент отказал в запросе (${status}).`
}
